import React, {useMemo, useRef, useState} from "react";
import {http} from '@firefly/fly-http';
import {isWechat, noop} from '@firefly/fly-defter';
import {useShallowCompareEffect, useMemoizedFn} from "@firefly/fly-hook";
import BridgeContext, {BridgeContextType} from './context/BridgeContext';
import {WechatSDK} from './lib/sdk';
import {ReadyFunction} from './typing';

interface WechatAppBridgeProps {
    enabled?: boolean;
    configKey?: string[];
    debug?: boolean;
    jsApis?: string[];
    onError?: (reason: any) => void;
    children: React.ReactNode;
}

const JS_APIS: string[] = [
    'getNetworkType',
    'hideMenuItems',
    'showMenuItems',
    'updateAppMessageShareData',
    'updateTimelineShareData',
    'chooseImage',
    'uploadImage',
    'previewImage',
    'downloadImage',
    'getLocalImgData',
    'openLocation',
    'getLocation',
    'chooseWXPay',
];

function checkJSAPI(apis: string[]): Promise<Record<string, boolean>> {
    return new Promise<Record<string, boolean>>((resolve, reject) => {
        wx.checkJsApi({
            jsApiList: apis, // 需要检测的 JS 接口列表，所有 JS 接口列表见附录2,
            success: function (res: any) {
                // 以键值对的形式返回，可用的 api 值true，不可用为false
                // 如：{"checkResult":{"chooseImage":true},"errMsg":"checkJsApi:ok"}
                const result = res.checkResult;
                resolve(result);
            },
            fail: function () {
                reject();
            },
        });
    });
}

const isWechatEnabled = isWechat();

function WechatAppBridge(props: WechatAppBridgeProps) {
    const {
        enabled = true,
        configKey = [],
        debug = false,
        jsApis = JS_APIS,
        onError : _onError = noop,
        children,
    } = props;


    const supported = useRef<string[]>([]);


    const ContextValue = useMemo<BridgeContextType>(() => {
        return {
            ready: (callback: ReadyFunction) => {
                if (isWechatEnabled) {
                    WechatSDK.configReady(callback);
                }
            },
            support: async (api: string) => {
                if (!isWechatEnabled) {
                    return false;
                }
                const reject = !supported.current.includes(api);
                if (reject) {
                    return await checkJSAPI([api]).then((result) => {
                        const pass = result[api];
                        if (pass) {
                            supported.current.push(api);
                        }
                        return pass!;
                    });
                }
                return true;
            },
        };
    }, []);

    const [url] = useState(() => {
        return window.location.href;
    });

    const onError = useMemoizedFn(_onError)
    useShallowCompareEffect(() => {
        if (enabled && isWechatEnabled) {
            let canceled = false;
            // const url = window.location.href
            Promise.all([
                http.post(`/api/wechat/js/config`, {
                    url: url,
                }),
            ]).then(([options]) => {
                if (canceled) {
                    return;
                }
                return WechatSDK.config({
                    ...options,
                    jsApiList: jsApis,
                    openTagList: ['wx-open-launch-weapp'],
                    debug: debug,
                }).catch((reason) => {
                    console.error("WechatBridge", reason);
                    onError(reason);
                });
            });
            return () => {
                canceled = true;
            }
        }
        return () => {

        }
    }, [enabled, isWechatEnabled, configKey]);
    return (
        <BridgeContext.Provider value={ContextValue}>
            {children}
        </BridgeContext.Provider>
    );
}

export default WechatAppBridge;
